import { useState, useEffect, useMemo } from 'react';

import useAuthStore from '@entities/auth/model/useAuthStore';
// 서명 이미지 React Query 훅: 백엔드 pc.xml에서 BLOB 매핑을 위해 resultMap 사용 필요 (resultType만 사용 시 null 반환)
import { useSignImageQueries } from '@features/approval/hooks';
import safeToBase64Image from '@shared/lib/safeToBase64Image';

import ApprovalLineModal from '../ApprovalLineModal/ApprovalLineModal';

import styles from './ApprovalLineTable.module.scss';

/**
 * 결재 상태에 따른 표시 문구 반환
 * @param {number} state - 결재 상태 (1: 대기, 2: 현재 결재 차례, 3: 결재 완료, 4: 반려, 6: 수정요청)
 * @param {string} type - 결재 타입 ('draft', 'approval', 'review', 'agreement')
 * @param {string} tabType - 탭 타입 ('approvalIng', 'futureIng', 'done', 'ing', 'return')
 * @returns {string} 상태 문구
 */
function getStatusText(state, type, tabType) {
  // 결재 완료(state === 3)면 문구 없음 (서명 이미지 표시)
  if (state === 3) return null;

  // 반려 상태 (state === 4)
  if (state === 4) return '반려';

  // 수정요청 상태 (state === 6)
  if (state === 6) return '수정요청';

  const typeTextMap = {
    draft: { ing: '기안중', waiting: '기안대기' },
    approval: { ing: '결재중', waiting: '결재대기' },
    review: { ing: '검토중', waiting: '검토대기' },
    agreement: { ing: '합의중', waiting: '합의대기' },
  };

  const textSet = typeTextMap[type] || typeTextMap.approval;

  // 모든 탭에서 실제 상태에 따라 표시
  // state === 2: 현재 결재 차례 → "결재중"
  // state === 1: 대기 상태 → "결재대기"
  if (state === 2) return textSet.ing;
  if (state === 1) return textSet.waiting;

  return null;
}

/**
 * 기안 테이블 컴포넌트
 * - 기안(세로) | 직급(변수, 빈칸) | 결재(세로) | 직급(변수, 빈칸) | 직급(변수, 빈칸) | 직급(변수, 빈칸) | 직급(변수, 빈칸)
 * - 결재 다음에 4칸 (선택 가능한 칸 4개)
 * - 헤더의 직급은 선택한 직원의 직급을 동적으로 표시 (없으면 빈칸)
 */
function DraftTable({
  drafter,
  approvers,
  onCellClick,
  getApproverDisplay,
  isPreview,
  isDrafterReadOnly,
  signImages,
  tabType,
}) {
  return (
    <table className={styles.approvalTable}>
      <tbody>
        {/* 첫 번째 행: 직책 헤더 */}
        <tr>
          {/* 기안 - 2줄 차지, 회색 배경 */}
          <td rowSpan={2} className={styles.verticalHeader}>
            기안
          </td>
          {/* 직급 - 흰색 배경 (선택한 직원의 직급 표시, 없으면 빈칸) */}
          <td className={styles.headerCell}>
            {getApproverDisplay(drafter.fieldName).position || ''}
          </td>
          {/* 결재 - 2줄 차지, 회색 배경 */}
          <td rowSpan={2} className={styles.mergedHeader}>
            결재
          </td>
          {/* 결재자들의 직급 - 흰색 배경 (선택한 직원의 직급 표시, 없으면 빈칸, 4칸) */}
          {approvers.map((approver, index) => (
            <td key={`role-${index}`} className={styles.headerCell}>
              {getApproverDisplay(approver.fieldName).position || ''}
            </td>
          ))}
        </tr>
        {/* 두 번째 행: 이름 및 선택 영역 */}
        <tr>
          {/* 이름 - 기안1은 읽기 전용 (로그인한 사용자 정보 자동 입력) */}
          <td
            className={
              isDrafterReadOnly
                ? styles.nameCellWrapper
                : !isPreview
                  ? styles.selectableCell
                  : styles.nameCellWrapper
            }
          >
            <div className={styles.cellContent}>
              <div
                className={styles.nameCell}
                title={getApproverDisplay(drafter.fieldName).name || ''}
              >
                {(() => {
                  const name =
                    getApproverDisplay(drafter.fieldName).name ||
                    (!isPreview && !isDrafterReadOnly ? '선택' : '');
                  return name && name.length > 3 ? `${name.substring(0, 3)}...` : name;
                })()}
              </div>
              {/* 기안자 서명 이미지 (상신 완료 시 - state === 3) */}
              {isPreview &&
                getApproverDisplay(drafter.fieldName).state === 3 &&
                signImages?.sign_1 && (
                  <img src={signImages.sign_1} alt="서명" className={styles.signatureImage} />
                )}
              {/* 기안자 상태 문구 표시 (반려, 수정요청 등) */}
              {isPreview &&
                (() => {
                  const display = getApproverDisplay(drafter.fieldName);
                  const statusText = getStatusText(display.state, 'draft', tabType);
                  return statusText && display.name ? (
                    <span className={styles.statusText}>{statusText}</span>
                  ) : null;
                })()}
            </div>
          </td>
          {/* 결재자들 이름 (4칸) */}
          {approvers.map((approver, index) => {
            const display = getApproverDisplay(approver.fieldName);
            const name = display.name || (!isPreview ? '선택' : '');
            const state = display.state;
            const signKey = `sign_${index + 2}`; // sign_2, sign_3, sign_4, sign_5
            const signImage = signImages?.[signKey];
            const statusText = getStatusText(state, 'approval', tabType);

            return (
              <td
                key={`name-${index}`}
                className={!isPreview ? styles.selectableCell : styles.nameCellWrapper}
                onClick={() => onCellClick && onCellClick()}
              >
                <div className={styles.cellContent}>
                  <div className={styles.nameCell} title={name}>
                    {name && name.length > 3 ? `${name.substring(0, 3)}...` : name}
                  </div>
                  {/* 결재 완료 시 서명 이미지 표시 (state === 3) */}
                  {isPreview && state === 3 && signImage && (
                    <img src={signImage} alt="서명" className={styles.signatureImage} />
                  )}
                  {/* 결재 대기/진행 중 상태 문구 표시 */}
                  {isPreview && statusText && display.name && (
                    <span className={styles.statusText}>{statusText}</span>
                  )}
                </div>
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
}

/**
 * 검토 테이블 컴포넌트
 * - 검토(세로) | 직급(변수, 빈칸) | 직급(변수, 빈칸) | 직급(변수, 빈칸) | 직급(변수, 빈칸) | 합의(세로) | 직급(변수, 빈칸)
 * - 검토 다음에 4칸: 모두 선택 가능한 칸
 * - 합의 다음에 1칸: 선택 가능한 칸 1개
 * - 헤더의 직급은 선택한 직원의 직급을 동적으로 표시 (없으면 빈칸)
 */
function ReviewTable({
  reviewApprovers,
  onCellClick,
  getApproverDisplay,
  isPreview,
  signImages,
  tabType,
}) {
  return (
    <table className={styles.approvalTable}>
      {/* colgroup: 검토 헤더(25px), 검토자1-4 직급(각 40px), 합의 헤더(25px), 합의자 이름(auto) */}
      <colgroup>
        <col style={{ width: '25px' }} />
        <col style={{ width: '40px' }} />
        <col style={{ width: '40px' }} />
        <col style={{ width: '40px' }} />
        <col style={{ width: '40px' }} />
        <col style={{ width: '25px' }} />
        <col style={{ width: 'auto' }} />
      </colgroup>
      <tbody>
        {/* 첫 번째 행: 직책 헤더 */}
        <tr>
          {/* 검토 - 2줄 차지, 회색 배경 */}
          <td rowSpan={2} className={styles.verticalHeader}>
            검토
          </td>
          {/* 검토 다음 4칸의 직급 - 흰색 배경 (선택한 직원의 직급 표시, 없으면 빈칸) */}
          {reviewApprovers.map((approver, index) => (
            <td key={`role-${index}`} className={styles.headerCell}>
              {getApproverDisplay(approver.fieldName).position || ''}
            </td>
          ))}
          {/* 합의 - 2줄 차지, 회색 배경 */}
          <td rowSpan={2} className={styles.mergedHeader}>
            합의
          </td>
          {/* 직급 - 흰색 배경 (선택한 직원의 직급 표시, 없으면 빈칸, 합의 다음 1칸) */}
          <td className={styles.headerCell}>{getApproverDisplay('agreement1').position || ''}</td>
        </tr>
        {/* 두 번째 행: 이름 및 선택 영역 */}
        <tr>
          {/* 검토 다음 4칸의 이름 */}
          {reviewApprovers.map((approver, index) => {
            const display = getApproverDisplay(approver.fieldName);
            const name = display.name || (!isPreview ? '선택' : '');
            const state = display.state;
            const signKey = `check_${index + 1}`; // check_1, check_2, check_3, check_4
            const signImage = signImages?.[signKey];
            const statusText = getStatusText(state, 'review', tabType);

            return (
              <td
                key={`name-${index}`}
                className={!isPreview ? styles.selectableCell : styles.nameCellWrapper}
                onClick={() => onCellClick && onCellClick()}
              >
                <div className={styles.cellContent}>
                  <div className={styles.nameCell} title={name}>
                    {name && name.length > 3 ? `${name.substring(0, 3)}...` : name}
                  </div>
                  {/* 검토 완료 시 서명 이미지 표시 (state === 3) */}
                  {isPreview && state === 3 && signImage && (
                    <img src={signImage} alt="서명" className={styles.signatureImage} />
                  )}
                  {/* 검토 대기/진행 중 상태 문구 표시 */}
                  {isPreview && statusText && display.name && (
                    <span className={styles.statusText}>{statusText}</span>
                  )}
                </div>
              </td>
            );
          })}
          {/* 이름 (합의 다음 1칸) */}
          {(() => {
            const display = getApproverDisplay('agreement1');
            const name = display.name || (!isPreview ? '선택' : '');
            const state = display.state;
            const signImage = signImages?.agreement;
            const statusText = getStatusText(state, 'agreement', tabType);

            return (
              <td
                className={!isPreview ? styles.selectableCell : styles.nameCellWrapper}
                onClick={() => onCellClick && onCellClick()}
              >
                <div className={styles.cellContent}>
                  <div className={styles.nameCell} title={name}>
                    {name && name.length > 3 ? `${name.substring(0, 3)}...` : name}
                  </div>
                  {/* 합의 완료 시 서명 이미지 표시 (state === 3) */}
                  {isPreview && state === 3 && signImage && (
                    <img src={signImage} alt="서명" className={styles.signatureImage} />
                  )}
                  {/* 합의 대기/진행 중 상태 문구 표시 */}
                  {isPreview && statusText && display.name && (
                    <span className={styles.statusText}>{statusText}</span>
                  )}
                </div>
              </td>
            );
          })()}
        </tr>
      </tbody>
    </table>
  );
}

/**
 * ApprovalLineTable 컴포넌트
 * - 결재선 테이블 컴포넌트
 * - 순수 HTML table 태그 사용
 * - 피그마 기획 기반: 기안 테이블과 검토 테이블로 분리
 * - DraftTable과 ReviewTable 컴포넌트를 사용하여 구조 단순화
 *
 * @param {Object} documentInfo - 문서 정보
 * @param {Function} onDocumentInfoChange - 문서 정보 변경 핸들러
 * @param {boolean} isPreview - 미리보기 모드 여부
 * @param {number} docIdx - 문서 인덱스 (서명 이미지 조회용)
 * @param {string} tabType - 탭 타입 ('approvalIng', 'futureIng', 'done')
 * @param {number} formId - 양식 ID (검토자 정보 조회용, 선택적)
 * @param {boolean} isApprovalLineModified - 결재라인 사용자 수정 여부
 * @param {Function} onApprovalLineChange - 결재라인 변경 시 호출되는 콜백 (선택적)
 */
function ApprovalLineTable({
  documentInfo,
  onDocumentInfoChange,
  isPreview = false,
  docIdx,
  tabType,
  formId = null,
  isApprovalLineModified = false,
  onApprovalLineChange = null,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const { user } = useAuthStore();

  // 결재선 정보 (documentInfo에서 가져오거나 기본값)
  const approvalLine = documentInfo.approvalLine || {
    draft1: null,
    approval1: null,
    approval2: null,
    approval3: null,
    approval4: null,
    review1: null,
    review2: null,
    review3: null,
    review4: null,
    agreement: null,
    agreement1: null,
  };

  // 서명 이미지 조회를 위한 결재자 목록 생성
  // state === 3 (결재 완료)인 경우에만 조회
  const signersToFetch = useMemo(
    function () {
      if (!isPreview || !docIdx) return [];

      const signers = [];

      // 기안자 (sign_1) - state가 3(상신완료)인 경우만 조회
      if (approvalLine.draft1?.enumber && approvalLine.draft1.state === 3) {
        signers.push({ key: 'sign_1', enumber: approvalLine.draft1.enumber });
      }

      // 결재자들 (sign_2 ~ sign_5) - state가 3(결재완료)인 경우만 조회
      for (let i = 1; i <= 4; i++) {
        const approver = approvalLine[`approval${i}`];
        if (approver?.enumber && approver.state === 3) {
          signers.push({ key: `sign_${i + 1}`, enumber: approver.enumber });
        }
      }

      // 검토자들 (check_1 ~ check_4) - state가 3(검토완료)인 경우만 조회
      for (let i = 1; i <= 4; i++) {
        const reviewer = approvalLine[`review${i}`];
        if (reviewer?.enumber && reviewer.state === 3) {
          signers.push({ key: `check_${i}`, enumber: reviewer.enumber });
        }
      }

      // 합의자 (agreement) - state가 3(합의완료)인 경우만 조회
      if (approvalLine.agreement1?.enumber && approvalLine.agreement1.state === 3) {
        signers.push({ key: 'agreement', enumber: approvalLine.agreement1.enumber });
      }

      return signers;
    },
    [
      isPreview,
      docIdx,
      approvalLine.draft1,
      approvalLine.approval1,
      approvalLine.approval2,
      approvalLine.approval3,
      approvalLine.approval4,
      approvalLine.review1,
      approvalLine.review2,
      approvalLine.review3,
      approvalLine.review4,
      approvalLine.agreement1,
    ],
  );

  // React Query를 사용한 서명 이미지 조회 (캐싱 및 중복 요청 방지)
  const { data: rawSignImages } = useSignImageQueries(docIdx, signersToFetch);

  // 서명 이미지 데이터를 base64로 변환
  const signImages = useMemo(
    function () {
      const images = {};
      Object.keys(rawSignImages || {}).forEach(function (key) {
        images[key] = safeToBase64Image(rawSignImages[key]);
      });
      return images;
    },
    [rawSignImages],
  );

  // 로그인한 사용자 정보를 기안1에 자동으로 설정
  // 단, 임시저장 문서의 경우 이미 설정된 기안자 정보를 덮어쓰지 않음
  // 중요: 결재라인(approval1~4, review1~4, agreement1)이 이미 설정되어 있으면 건드리지 않음
  useEffect(
    function () {
      if (!user || !onDocumentInfoChange || isPreview) {
        return;
      }

      const currentApprovalLine = documentInfo.approvalLine || {};
      const currentDraft1 = currentApprovalLine.draft1;

      // 기안1이 이미 설정되어 있으면 (enumber가 있으면) 건드리지 않음
      if (currentDraft1?.enumber) {
        return;
      }

      // 결재라인이 이미 설정되어 있으면 (임시저장 문서 로드 중일 수 있음) 건드리지 않음
      const hasApprovalLineData =
        currentApprovalLine.approval1 ||
        currentApprovalLine.approval2 ||
        currentApprovalLine.approval3 ||
        currentApprovalLine.approval4 ||
        currentApprovalLine.review1 ||
        currentApprovalLine.review2 ||
        currentApprovalLine.review3 ||
        currentApprovalLine.review4 ||
        currentApprovalLine.agreement1;

      if (hasApprovalLineData) {
        return;
      }

      // 기안1이 완전히 비어있고, 결재라인도 없을 때만 자동으로 설정
      onDocumentInfoChange(function (prev) {
        // 이미 설정되어 있으면 업데이트하지 않음
        if (prev.approvalLine?.draft1?.enumber) {
          return prev;
        }

        return {
          ...prev,
          approvalLine: {
            ...prev.approvalLine,
            draft1: {
              enumber: user.enumber || 0,
              name: user.name || '',
              position: user.position || '',
            },
          },
        };
      });
    },
    // 의존성을 최소화하여 무한 루프 방지
    [user?.enumber, onDocumentInfoChange, isPreview, documentInfo.approvalLine?.draft1?.enumber],
  );

  // 모달 열기 핸들러 (아무 칸이나 클릭해도 같은 모달 열림)
  function handleCellClick() {
    if (isPreview) {
      return;
    }
    setModalOpen(true);
  }

  // 결재자 선택 핸들러 (모든 결재선을 한 번에 업데이트)
  function handleApproverSelect(employees) {
    if (!employees || !onDocumentInfoChange) {
      setModalOpen(false);
      return;
    }

    // 빈 배열인 경우 모든 결재선을 null로 초기화
    if (employees.length === 0) {
      onDocumentInfoChange(function (prev) {
        const updatedApprovalLine = { ...prev.approvalLine };

        // 모든 결재자 필드를 null로 초기화
        for (let i = 1; i <= 4; i++) {
          updatedApprovalLine[`approval${i}`] = null;
        }

        // 모든 검토자 필드를 null로 초기화
        for (let i = 1; i <= 4; i++) {
          updatedApprovalLine[`review${i}`] = null;
        }

        // 합의자 필드를 null로 초기화
        updatedApprovalLine.agreement1 = null;

        return {
          ...prev,
          approvalLine: updatedApprovalLine,
        };
      });

      if (onApprovalLineChange) {
        onApprovalLineChange();
      }

      setModalOpen(false);
      return;
    }

    // approvalLine 업데이트
    onDocumentInfoChange(function (prev) {
      const updatedApprovalLine = { ...prev.approvalLine };

      // 전역 순서(모달에서 드래그로 정한 순서)를 유지하기 위해
      // 직원 목록의 index 기반으로 order를 부여합니다.
      // - 기안자(sign1)는 order=1
      // - 첫 번째 결재/검토/합의자는 order=2, 이후 3,4...
      // 서버는 order 기반으로 "다음 결재자"를 결정합니다.
      let approvalIndex = 1;
      let reviewIndex = 1;
      let hasAgreement = false;

      // 먼저 전체 슬롯을 초기화
      for (let i = 1; i <= 4; i++) {
        updatedApprovalLine[`approval${i}`] = null;
        updatedApprovalLine[`review${i}`] = null;
      }
      updatedApprovalLine.agreement1 = null;

      employees.forEach(function (employee, index) {
        const order = index + 2;
        const payload = {
          enumber: employee.enumber || 0,
          name: employee.name || '',
          position: employee.position || '',
          team: employee.team || '',
          order,
        };

        if (employee.role === '결재' && approvalIndex <= 4) {
          updatedApprovalLine[`approval${approvalIndex}`] = payload;
          approvalIndex += 1;
          return;
        }

        if (employee.role === '검토' && reviewIndex <= 4) {
          updatedApprovalLine[`review${reviewIndex}`] = payload;
          reviewIndex += 1;
          return;
        }

        if (employee.role === '합의' && !hasAgreement) {
          updatedApprovalLine.agreement1 = payload;
          hasAgreement = true;
        }
      });

      return {
        ...prev,
        approvalLine: updatedApprovalLine,
      };
    });

    // 사용자가 결재라인을 수정했음을 부모 컴포넌트에 알림
    if (onApprovalLineChange) {
      onApprovalLineChange();
    }

    setModalOpen(false);
  }

  // 결재자 정보 표시 함수
  function getApproverDisplay(fieldName) {
    const approver = approvalLine[fieldName];
    if (!approver) {
      return { name: '', position: '', state: null };
    }
    return {
      name: approver.name || '',
      position: approver.position || '',
      state: approver.state,
    };
  }

  // 피그마 디자인: 항상 기안+결재 테이블 / 검토+합의 테이블 2단 구성으로 표시
  return (
    <>
      <div className={styles.approvalLineContainer}>
        <DraftTable
          drafter={{ fieldName: 'draft1', position: '기안-1' }}
          approvers={[
            { fieldName: 'approval1', position: '기안-결재1' },
            { fieldName: 'approval2', position: '기안-결재2' },
            { fieldName: 'approval3', position: '기안-결재3' },
            { fieldName: 'approval4', position: '기안-결재4' },
          ]}
          onCellClick={handleCellClick}
          getApproverDisplay={getApproverDisplay}
          isPreview={isPreview}
          isDrafterReadOnly
          signImages={signImages}
          tabType={tabType}
        />
        <ReviewTable
          reviewApprovers={[
            { fieldName: 'review1', position: '검토-1' },
            { fieldName: 'review2', position: '검토-2' },
            { fieldName: 'review3', position: '검토-3' },
            { fieldName: 'review4', position: '검토-4' },
          ]}
          onCellClick={handleCellClick}
          getApproverDisplay={getApproverDisplay}
          isPreview={isPreview}
          signImages={signImages}
          tabType={tabType}
        />

        {!isPreview && (
          <ApprovalLineModal
            isOpen={modalOpen}
            onClose={() => {
              setModalOpen(false);
            }}
            onSelect={handleApproverSelect}
            approvalLine={approvalLine}
            formId={formId}
            isApprovalLineModified={isApprovalLineModified}
          />
        )}
      </div>
    </>
  );
}

export default ApprovalLineTable;
